/**
 * Staff chat query keys and the pure cache reducers (SR-14).
 *
 * The reducers take cache data and return new data; they never mutate. The realtime hook and the mutations both
 * use them, so a hub event and an optimistic send land in the cache the same way:
 *   - `upsertMessage`: a message joins (newest first) or replaces the copy with the same id, or the optimistic
 *     copy with the same sender + clientMessageId (so a send echoed back by the hub never shows twice);
 *   - `applyMessageToThreads`: the thread's last message, its place at the top, and its unread count;
 *   - `setThreadUnread` / `upsertThread`: the read marker and a new or changed thread.
 */
import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import type { StaffChatLastMessage, StaffChatMessage, StaffChatPage, StaffChatThread } from '../staff-chat-types';

/** The root of the live read markers' keys (outside `staffChatKeys.all`, see `reads`). */
export const STAFF_CHAT_READS_ROOT = 'staff-chat-reads';

export const staffChatKeys = {
  all: ['staff-chat'] as const,
  threads: () => [...staffChatKeys.all, 'threads'] as const,
  thread: (threadId: string) => [...staffChatKeys.all, 'thread', threadId] as const,
  messages: (threadId: string) => [...staffChatKeys.all, 'messages', threadId] as const,
  directory: () => [...staffChatKeys.all, 'directory'] as const,
  unread: () => [...staffChatKeys.all, 'unread'] as const,
  /**
   * Live read markers of a thread's other participants: userId → readAt (ISO), from `StaffChatRead` events only
   * (the thread DTO carries no lastReadAt, so after a reload the dock says "Sent" until the next read event).
   * Outside the `all` prefix: nothing to refetch on reconnect.
   */
  reads: (threadId: string) => [STAFF_CHAT_READS_ROOT, threadId] as const,
};

export type ReadMarkers = Record<string, string>;

/**
 * An ISO instant as epoch ms for comparing (never compare the strings: the server's precision varies, so
 * "08:00:00Z" would sort after "08:00:00.5Z"). An unreadable value sorts first.
 */
export function instant(iso: string | null | undefined): number {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? t : -Infinity;
}

/** Keeps the later readAt per user. */
export function mergeReadMarker(data: ReadMarkers | undefined, userId: string, readAt: string): ReadMarkers {
  const prev = data?.[userId];
  return { ...(data ?? {}), [userId]: prev && instant(prev) > instant(readAt) ? prev : readAt };
}

export type ThreadsData = StaffChatPage<StaffChatThread>;
export type MessagesData = InfiniteData<StaffChatPage<StaffChatMessage>, string | null>;

const PREVIEW_MAX = 140;

function sameMessage(a: StaffChatMessage, b: StaffChatMessage): boolean {
  if (a.id === b.id) return true;
  return a.clientMessageId !== null && a.clientMessageId === b.clientMessageId && a.senderUserId === b.senderUserId;
}

/** Whether the cached pages already hold this message (by id, or as the optimistic copy). */
export function hasMessage(data: MessagesData | undefined, message: StaffChatMessage): boolean {
  return !!data?.pages.some((p) => p.items.some((m) => sameMessage(m, message)));
}

/** Adds the message at the newest end, or replaces the copy it matches. Undefined stays undefined (not loaded). */
export function upsertMessage(data: MessagesData | undefined, message: StaffChatMessage): MessagesData | undefined {
  if (!data) return data;
  if (hasMessage(data, message)) {
    return {
      ...data,
      pages: data.pages.map((p) => ({ ...p, items: p.items.map((m) => (sameMessage(m, message) ? message : m)) })),
    };
  }
  const [first, ...rest] = data.pages;
  const head: StaffChatPage<StaffChatMessage> = first
    ? { ...first, items: [message, ...first.items] }
    : { items: [message], nextCursor: null };
  return { ...data, pages: [head, ...rest] };
}

/** Sets the local status of the optimistic message with this clientMessageId (`undefined` clears it). */
export function setLocalStatus(
  data: MessagesData | undefined,
  clientMessageId: string,
  localStatus: StaffChatMessage['localStatus'],
): MessagesData | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((p) => ({
      ...p,
      items: p.items.map((m) => (m.clientMessageId === clientMessageId && m.localStatus ? { ...m, localStatus } : m)),
    })),
  };
}

/** Oldest first, for display (the API pages newest first). */
export function messagesOldestFirst(data: { pages: StaffChatPage<StaffChatMessage>[] } | undefined): StaffChatMessage[] {
  if (!data) return [];
  return data.pages.flatMap((p) => p.items).slice().reverse();
}

/** The newest live (not deleted, not optimistic) message in the cached pages, as a thread's last message. */
export function newestLiveMessage(data: MessagesData | undefined): StaffChatLastMessage | null {
  const m = data?.pages.flatMap((p) => p.items).find((x) => !x.isDeleted && !x.localStatus);
  return m ? { id: m.id, senderUserId: m.senderUserId, preview: m.body.slice(0, PREVIEW_MAX), createdAt: m.createdAt } : null;
}

/**
 * A message posted (or deleted) in a thread: its last message, its place at the top of the list, and, for a new
 * message from someone else, one more unread. `found: false` when the thread is not in the list (the caller
 * refetches the list).
 *
 * Deleting the thread's last message makes the previous live message the last one, or none (contract). The
 * caller passes it as `previousLive` from the loaded messages; `undefined` (not loaded) leaves the thread as it was
 * and answers `stale: true`, so the caller refetches the list.
 */
export function applyMessageToThreads(
  data: ThreadsData | undefined,
  message: StaffChatMessage,
  opts: { meUserId: string | null | undefined; isNew: boolean; previousLive?: StaffChatLastMessage | null },
): { data: ThreadsData | undefined; found: boolean; stale?: boolean } {
  if (!data) return { data, found: false };
  const index = data.items.findIndex((t) => t.id === message.threadId);
  if (index < 0) return { data, found: false };
  const thread = data.items[index];
  if (message.isDeleted) {
    if (thread.lastMessage?.id !== message.id) return { data, found: true };
    if (opts.previousLive === undefined) return { data, found: true, stale: true };
    const deleted: StaffChatThread = { ...thread, lastMessage: opts.previousLive };
    return { data: { ...data, items: data.items.map((t, i) => (i === index ? deleted : t)) }, found: true };
  }
  // An optimistic send is the newest by definition (its time is the browser clock, which may lag the server's), and
  // the server's copy of it takes its place.
  const isLatest =
    message.localStatus === 'sending' ||
    !thread.lastMessage ||
    thread.lastMessage.id === message.id ||
    (message.clientMessageId !== null && thread.lastMessage.id === `local:${message.clientMessageId}`) ||
    instant(thread.lastMessage.createdAt) <= instant(message.createdAt);
  const fromOther = message.senderUserId !== opts.meUserId;
  const updated: StaffChatThread = {
    ...thread,
    lastMessage: isLatest
      ? { id: message.id, senderUserId: message.senderUserId, preview: message.body.slice(0, PREVIEW_MAX), createdAt: message.createdAt }
      : thread.lastMessage,
    unreadCount: opts.isNew && fromOther ? thread.unreadCount + 1 : thread.unreadCount,
    updatedAt: isLatest && instant(message.createdAt) > instant(thread.updatedAt) ? message.createdAt : thread.updatedAt,
  };
  const others = data.items.filter((_, i) => i !== index);
  return { data: { ...data, items: isLatest && opts.isNew ? [updated, ...others] : data.items.map((t, i) => (i === index ? updated : t)) }, found: true };
}

/** The caller's unread count of one thread. */
export function setThreadUnread(data: ThreadsData | undefined, threadId: string, unreadCount: number): ThreadsData | undefined {
  if (!data) return data;
  return { ...data, items: data.items.map((t) => (t.id === threadId ? { ...t, unreadCount } : t)) };
}

/** A new or changed thread: replaced in place, or added at the top. */
export function upsertThread(data: ThreadsData | undefined, thread: StaffChatThread): ThreadsData | undefined {
  if (!data) return data;
  const exists = data.items.some((t) => t.id === thread.id);
  return { ...data, items: exists ? data.items.map((t) => (t.id === thread.id ? thread : t)) : [thread, ...data.items] };
}

/** The sum of the listed threads' unread counts. */
export function sumUnread(data: ThreadsData | undefined): number {
  return (data?.items ?? []).reduce((n, t) => n + t.unreadCount, 0);
}

// ---------------------------------------------------------------------------
// Query-client helpers (one place that writes each cache)
// ---------------------------------------------------------------------------

/** A message from the server (hub event or POST answer) into every cache it touches. */
export function writeMessage(qc: QueryClient, message: StaffChatMessage, meUserId: string | null | undefined): void {
  const key = staffChatKeys.messages(message.threadId);
  const before = qc.getQueryData<MessagesData>(key);
  const isNew = !hasMessage(before, message);
  qc.setQueryData<MessagesData>(key, (d) => upsertMessage(d, message));
  const after = qc.getQueryData<MessagesData>(key);
  const previousLive = message.isDeleted && after ? newestLiveMessage(after) : undefined;
  let refetch = false;
  qc.setQueryData<ThreadsData>(staffChatKeys.threads(), (d) => {
    const r = applyMessageToThreads(d, message, { meUserId, isNew, previousLive });
    refetch = !r.found || !!r.stale;
    return r.data;
  });
  if (refetch) void qc.invalidateQueries({ queryKey: staffChatKeys.threads() });
}

/** The caller read a thread: its count to 0, and the total down by what it held. */
export function writeThreadRead(qc: QueryClient, threadId: string): void {
  const held = qc.getQueryData<ThreadsData>(staffChatKeys.threads())?.items.find((t) => t.id === threadId)?.unreadCount ?? 0;
  qc.setQueryData<ThreadsData>(staffChatKeys.threads(), (d) => setThreadUnread(d, threadId, 0));
  qc.setQueryData<StaffChatThread>(staffChatKeys.thread(threadId), (t) => (t ? { ...t, unreadCount: 0 } : t));
  if (held > 0) qc.setQueryData<number>(staffChatKeys.unread(), (n) => (typeof n === 'number' ? Math.max(0, n - held) : n));
}
